/**
 * Карточка дела: боковая навигация по блокам + область содержимого (ТЗ FR-02).
 *
 * Пошаговый мастер не применяется сознательно: пользователь-профессионал
 * должен иметь произвольный доступ к любому блоку. Дело редко заполняется
 * сверху вниз — сведения приходят по мере получения ответов от органов.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { BLOCK_TITLES, CASE_PRESETS, isPresetApplicable, type BlockId } from '@bd/domain';
import type { CaseContext } from '../../bx24.js';
import { currentUser } from '../../storage/cases.js';
import { useProfile } from './useProfile.js';
import { useAutosave } from './useAutosave.js';
import { useLock } from './useLock.js';
import { usePostalCodeOnOpen } from './usePostalCodeOnOpen.js';
import { ReadinessPanel } from '../readiness/ReadinessPanel.js';
import { GeneratePanel } from '../generation/GeneratePanel.js';
import { PreviewPane } from '../generation/PreviewPane.js';
import { HistoryPanel } from './HistoryPanel.js';
import { CrmImportBanner } from './CrmImportBanner.js';
import { DebtorBlock } from './blocks/DebtorBlock.js';
import { CourtBlock } from './blocks/CourtBlock.js';
import { RepresentativeBlock } from './blocks/RepresentativeBlock.js';
import { CreditorsBlock } from './blocks/CreditorsBlock.js';
import { PropertyBlock } from './blocks/PropertyBlock.js';
import { DealsBlock } from './blocks/DealsBlock.js';
import { FamilyBlock } from './blocks/FamilyBlock.js';
import { IncomeBlock } from './blocks/IncomeBlock.js';
import { LegalBlock } from './blocks/LegalBlock.js';
import { api } from '../../api.js';
import { useDictionaryFailures } from '../../storage/dictionary-health.js';

type Tab = BlockId | 'generate' | 'preview' | 'history';

export function CasePage({ context }: { context: CaseContext }) {
  const { profile, loading, error, dirty, folderId, prefilled, crm, update, save, replace, clear, readiness } =
    useProfile(context);
  const [user, setUser] = useState<{ id: string; name: string } | null>(null);
  const dictionaryFailures = useDictionaryFailures();
  const lock = useLock(context.caseId, user);
  const [tab, setTab] = useState<Tab>('debtor');
  /** Очистка дела: сначала вопрос, потом действие (29.10.2026) */
  const [clearing, setClearing] = useState<'ask' | 'busy' | null>(null);
  const [clearMessage, setClearMessage] = useState<string | null>(null);

  // Кто работает с делом: нужно блокировке (FR-05) и журналу (FR-48)
  useEffect(() => {
    void currentUser()
      .then(setUser)
      .catch(() => setUser({ id: '', name: '' }));
  }, []);

  const readOnly = lock.readOnly;
  const postalMessage = usePostalCodeOnOpen(profile, update, readOnly);
  const autosave = useAutosave({
    dirty,
    profile,
    save: () => save(user ?? undefined),
    readOnly,
  });

  /** Сообщение об ошибке по пути поля — общее для всех блоков. */
  const errorFor = useCallback(
    (path: string): string | null => readiness?.issues.find((issue) => issue.path === path)?.message ?? null,
    [readiness],
  );

  /** Предзаполнение суда по субъекту РФ адреса регистрации (правило R-01). */
  const onRegionDetected = useCallback(
    async (regionCode: string) => {
      if (profile?.court.id) return; // выбранный вручную суд не перебиваем
      try {
        const { court } = await api.courtByRegion(regionCode);
        if (!court) return;
        update((p) => ({
          ...p,
          court: { id: String(court.id ?? ''), name: court.name, address: court.address ?? '', regionCode },
        }));
      } catch {
        // Справочник недоступен — суд выбирается вручную
      }
    },
    [profile?.court.id, update],
  );

  /**
   * Очистить всё и заполнить заново. Несохранённое сначала сохраняется —
   * так и оно попадёт в «Историю дела» и его можно будет вернуть.
   */
  const clearCase = async () => {
    setClearing('busy');
    setClearMessage(null);
    try {
      await autosave.flush();
      await clear(user ?? undefined);
      setTab('debtor');
      setClearMessage('Данные дела очищены. Прежние сведения можно вернуть: «История дела» → резервные копии.');
    } catch (e) {
      setClearMessage(`Не удалось очистить: ${(e as Error).message}`);
    } finally {
      setClearing(null);
    }
  };

  const blockProps = useMemo(
    () => (profile ? { profile, update, readOnly, errorFor } : null),
    [profile, update, readOnly, errorFor],
  );

  if (loading) return <div className="loading">Загрузка дела…</div>;
  if (!profile || !readiness || !blockProps) return <div className="error-box">Не удалось открыть дело: {error}</div>;

  return (
    <div className="case-page">
      <header className="case-header">
        <div>
          <h1>{[profile.debtor.last, profile.debtor.first, profile.debtor.middle].filter(Boolean).join(' ') || 'Новое дело'}</h1>
          <span className="hint">Дело № {context.caseId} · редакция профиля {profile.revision}</span>
        </div>

        <div className="case-status">
          {/* Индикатор состояния сохранения (ТЗ FR-03) */}
          {autosave.status === 'saving' && <span className="saving">Сохранение…</span>}
          {autosave.status === 'saved' && <span className="saved">Сохранено {autosave.savedAt?.toLocaleTimeString('ru-RU')}</span>}
          {autosave.status === 'error' && <span className="error-text">Не сохранено: {autosave.error}</span>}
          {dirty && autosave.status !== 'saving' && <span className="hint">есть несохранённые изменения</span>}
          <button type="button" className="link-button" disabled={readOnly || !dirty} onClick={() => void autosave.flush()}>
            Сохранить сейчас
          </button>
          <button type="button" className="link-button danger" disabled={readOnly || clearing !== null} onClick={() => setClearing('ask')}>
            Очистить данные дела
          </button>
        </div>
      </header>

      {/*
        Отказ справочников виден сразу.

        Без этой полосы отказ сервера выглядит как пустые списки: ни судов,
        ни субъектов РФ, ни СРО. Юрист принимает это за особенность карточки
        и продолжает заполнять дело, хотя собрать документы всё равно
        не получится — а узнает он об этом только при формировании пакета.
      */}
      {dictionaryFailures > 0 && (
        <div className="banner banner-error">
          <strong>Справочники не загрузились.</strong> Списки судов, субъектов РФ, СРО и видов
          обязательств будут пустыми, а собрать пакет не удастся. Данные дела при этом сохраняются:
          они хранятся в карточке сделки, а не на сервере приложения.
          <span className="hint">
            Обновите страницу. Если не помогло — сообщите администратору: не отвечает сервер приложения.
          </span>
        </div>
      )}

      {clearing === 'ask' && (
        <div className="banner banner-error">
          <strong>Очистить все данные дела?</strong> Всё введённое в приложении — должник, кредиторы, имущество,
          семья, доходы, отметки — будет удалено, и дело откроется как новое: снова подставится то, что есть
          в карточке сделки. Прежние сведения останутся в «Истории дела», их можно восстановить.
          Сформированные документы на Диске не удаляются.
          <div className="toolbar">
            <button type="button" className="primary danger" onClick={() => void clearCase()}>
              Очистить
            </button>
            <button type="button" className="link-button" onClick={() => setClearing(null)}>
              Отмена
            </button>
          </div>
        </div>
      )}
      {clearing === 'busy' && <div className="note">Очищаю дело…</div>}
      {clearMessage && <div className="note">{clearMessage}</div>}

      {postalMessage && <div className="note">{postalMessage}</div>}

      {/* Чек-лист сделки уже заполнен менеджером — повторный ввод не нужен (FR-06) */}
      <CrmImportBanner
        snapshot={crm ?? null}
        profile={profile}
        readOnly={readOnly}
        prefilled={prefilled ?? []}
        onApply={(next) => update(() => next)}
      />

      {/* Заготовка предлагается только пустому делу: она ничего не затирает (FR-45) */}
      {!readOnly && isPresetApplicable(profile) && (
        <div className="banner banner-hint">
          Дело пустое. Можно сразу выставить признаки самого частого сочетания:
          {CASE_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className="link-button"
              title={preset.description}
              onClick={() => update((p) => preset.apply(p))}
            >
              {preset.title} — {preset.description}
            </button>
          ))}
        </div>
      )}

      {readOnly && (
        <div className="banner">
          Дело открыто пользователем {lock.heldBy ?? 'другим пользователем'} — режим только для чтения.
          Изменения сохранить не получится.
        </div>
      )}

      <div className="case-layout">
        {/*
          Внутренняя обёртка нужна для «липкого» меню.

          Прилипал сам столбец, и это давало наложение: браузер разрешает
          такому элементу сдвигаться в пределах ВСЕЙ сетки, а не своей строки.
          На узком окне панель готовности переходит вниз во всю ширину —
          и прокрученное меню ложилось поверх списка ошибок. Фона у меню нет,
          поэтому строки просто просвечивали друг через друга.

          Прилипает обёртка, а столбец остаётся на месте: движение
          ограничено его высотой, то есть своей строкой.
        */}
        <nav className="case-nav">
          <div className="case-nav-inner">
          {(Object.keys(BLOCK_TITLES) as BlockId[]).map((block) => (
            <button
              key={block}
              type="button"
              className={tab === block ? 'nav-item active' : 'nav-item'}
              onClick={() => setTab(block)}
            >
              {BLOCK_TITLES[block]}
              {readiness.blocks[block] === 'errors' && <span className="dot error" title="Есть ошибки" />}
              {readiness.blocks[block] === 'warnings' && <span className="dot warning" title="Есть предупреждения" />}
            </button>
          ))}

          <hr />

          <button type="button" className={tab === 'preview' ? 'nav-item active' : 'nav-item'} onClick={() => setTab('preview')}>
            Предпросмотр
          </button>
          <button type="button" className={tab === 'generate' ? 'nav-item active' : 'nav-item'} onClick={() => setTab('generate')}>
            Формирование пакета
          </button>
          <button type="button" className={tab === 'history' ? 'nav-item active' : 'nav-item'} onClick={() => setTab('history')}>
            История дела
          </button>
          </div>
        </nav>

        <main className="case-content">
          {tab === 'debtor' && <DebtorBlock {...blockProps} onRegionDetected={onRegionDetected} />}
          {tab === 'court' && <CourtBlock {...blockProps} />}
          {tab === 'representative' && <RepresentativeBlock {...blockProps} />}
          {tab === 'creditors' && <CreditorsBlock {...blockProps} />}
          {tab === 'property' && <PropertyBlock {...blockProps} />}
          {tab === 'deals' && <DealsBlock {...blockProps} />}
          {tab === 'family' && <FamilyBlock {...blockProps} />}
          {tab === 'income' && <IncomeBlock {...blockProps} />}
          {tab === 'legal' && <LegalBlock {...blockProps} />}
          {tab === 'preview' && <PreviewPane profile={profile} />}
          {tab === 'history' && (
            <HistoryPanel
              caseId={context.caseId}
              readOnly={readOnly}
              onRestored={async (restored) => {
                await replace(restored, user ?? undefined);
              }}
            />
          )}
          {tab === 'generate' && (
            <GeneratePanel
              caseId={context.caseId}
              profile={profile}
              readiness={readiness}
              readOnly={readOnly}
              folderId={folderId}
              beforeGenerate={async () => {
                await autosave.flush();
                return save(user ?? undefined);
              }}
            />
          )}
        </main>

        <ReadinessPanel readiness={readiness} activeBlock={tab as BlockId} onSelectBlock={(block) => setTab(block)} />
      </div>
    </div>
  );
}
